import React, { useEffect, useState } from "react";
import AdminSidebar from "./AdminSidebar";
import {
  getAdminExams,
  getSchedules,
  addSchedule,
  updateSchedule,
  deleteSchedule,
} from "../api/adminclient";
import AdminTopbar from "./Admintopbar";
import { apiErrorMessage } from "../api/auth";

const emptyForm = {
  startTime: "09:00",
  endTime: "12:00",
  capacity: 50,
  date: "",
};

const ITEMS_PER_PAGE = 5;

export default function AdminScheduling() {
  const [exams, setExams] = useState([]);
  const [examId, setExamId] = useState("");
  const [date, setDate] = useState("");
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [examsLoaded, setExamsLoaded] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState(emptyForm);
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);

  // -----------------------------------------
  // Load exams
  // -----------------------------------------
  useEffect(() => {
    getAdminExams()
      .then(({ data }) => {
        setExams(data);

        if (data.length > 0) {
          setExamId(data[0].id);
        } else {
          // No exams at all — nothing will ever trigger loadSlots,
          // so stop the spinner here instead of hanging forever.
          setLoading(false);
        }
      })
      .catch((err) => setError(apiErrorMessage(err, "Could not load exams.")))
      .finally(() => setExamsLoaded(true));
  }, []);

  // -----------------------------------------
  // Load schedule slots
  // -----------------------------------------
  const loadSlots = (id) => {
    if (!id) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError("");

    getSchedules(id)
      .then(({ data }) => {
        setSlots(data);
        setCurrentPage(1);
      })
      .catch((err) => setError(apiErrorMessage(err, "Could not load schedule slots.")))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!examId) return;
    loadSlots(examId);
  }, [examId]);

  // -----------------------------------------
  // Pagination calculations
  // -----------------------------------------
  const totalPages = Math.ceil(slots.length / ITEMS_PER_PAGE);

  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;

  const currentSlots = slots.slice(
    startIndex,
    startIndex + ITEMS_PER_PAGE
  );

  // -----------------------------------------
  // Add slot
  // -----------------------------------------
  const addSlot = async () => {
    setFormError("");

    if (
      !date ||
      !form.startTime ||
      !form.endTime ||
      !form.capacity
    ) {
      setFormError(
        "Please fill in date, time range and capacity."
      );
      return;
    }

    // Validate time
    if (form.startTime >= form.endTime) {
      setFormError("End time must be later than start time.");
      return;
    }

    // Validate capacity
    if (Number(form.capacity) <= 0) {
      setFormError("Capacity must be greater than 0.");
      return;
    }

    setSaving(true);

    try {
      await addSchedule({
        ...form,
        examId,
        date,
        capacity: Number(form.capacity),
        status: "Active",
      });

      setForm(emptyForm);

      await loadSlots(examId);
    } catch (err) {
      setFormError(apiErrorMessage(err, "Could not add slot."));
    } finally {
      setSaving(false);
    }
  };

  // -----------------------------------------
  // Toggle status
  // -----------------------------------------
  const toggleStatus = async (slot) => {
    try {
      await updateSchedule(slot.id, {
        status:
          slot.status === "Active" ? "Inactive" : "Active",
      });

      loadSlots(examId);
    } catch (err) {
      setError(apiErrorMessage(err, "Could not update slot status."));
    }
  };

  // -----------------------------------------
  // Delete slot
  // -----------------------------------------
  const handleDelete = async (id) => {
    if (!window.confirm("Delete this slot?")) return;

    try {
      await deleteSchedule(id);

      // Reload slots
      const result = await getSchedules(examId);

      const updatedSlots = result.data;

      setSlots(updatedSlots);

      // Fix pagination after deletion
      const newTotalPages = Math.ceil(
        updatedSlots.length / ITEMS_PER_PAGE
      );

      if (
        currentPage > newTotalPages &&
        newTotalPages > 0
      ) {
        setCurrentPage(newTotalPages);
      }

      if (updatedSlots.length === 0) {
        setCurrentPage(1);
      }
    } catch (err) {
      setError(apiErrorMessage(err, "Could not delete slot."));
    }
  };

  // -----------------------------------------
  // Pagination controls
  // -----------------------------------------
  const goToPage = (page) => {
    if (page < 1 || page > totalPages) return;

    setCurrentPage(page);
  };

  // Generate page numbers
  const getPageNumbers = () => {
    const pages = [];

    for (let i = 1; i <= totalPages; i++) {
      pages.push(i);
    }

    return pages;
  };

  return (
    <div className="admin-page-full d-flex">
      <AdminSidebar />

      <div className="admin-content">
        <AdminTopbar />

        <h2 className="h5 fw-bold mb-3">
          Exam Scheduling
        </h2>

        {error && (
          <div className="alert alert-warning py-2 small">
            {error}
          </div>
        )}

        <div className="admin-card p-3 p-md-4">

          {/* ============================= */}
          {/* NO EXAMS AT ALL */}
          {/* ============================= */}

          {examsLoaded && exams.length === 0 && !error && (
            <p className="text-muted small mb-3">
              No exams found. Add an exam first before scheduling slots.
            </p>
          )}

          {/* ============================= */}
          {/* ADD SLOT FORM */}
          {/* ============================= */}

          {exams.length > 0 && (
            <div className="d-flex flex-wrap align-items-end gap-2 mb-3">

              <div>
                <label className="form-label small text-muted mb-1 d-block">
                  Exam
                </label>

                <select
                  className="form-select form-select-sm"
                  style={{ width: 220 }}
                  value={examId}
                  onChange={(e) => {
                    setExamId(e.target.value);
                    setCurrentPage(1);
                  }}
                >
                  {exams.map((e) => (
                    <option key={e.id} value={e.id}>
                      {e.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="form-label small text-muted mb-1 d-block">
                  Date
                </label>

                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={date}
                  onChange={(e) =>
                    setDate(e.target.value)
                  }
                />
              </div>

              <div>
                <label className="form-label small text-muted mb-1 d-block">
                  Start
                </label>

                <input
                  type="time"
                  className="form-control form-control-sm"
                  value={form.startTime}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      startTime: e.target.value,
                    }))
                  }
                />
              </div>

              <div>
                <label className="form-label small text-muted mb-1 d-block">
                  End
                </label>

                <input
                  type="time"
                  className="form-control form-control-sm"
                  value={form.endTime}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      endTime: e.target.value,
                    }))
                  }
                />
              </div>

              <div>
                <label className="form-label small text-muted mb-1 d-block">
                  Capacity
                </label>

                <input
                  type="number"
                  min="1"
                  className="form-control form-control-sm"
                  style={{ width: 90 }}
                  value={form.capacity}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      capacity: e.target.value,
                    }))
                  }
                />
              </div>

              <button
                className="btn btn-primary btn-sm"
                onClick={addSlot}
                disabled={saving}
              >
                <i className="bi bi-plus-lg me-1" />

                {saving ? "Adding..." : "Add Slot"}
              </button>
            </div>
          )}

          {formError && (
            <div className="text-danger small mb-2">
              {formError}
            </div>
          )}

          {/* ============================= */}
          {/* LOADING */}
          {/* ============================= */}

          {loading && exams.length > 0 && (
            <p className="text-muted mb-0">
              Loading slots...
            </p>
          )}

          {/* ============================= */}
          {/* EMPTY STATE */}
          {/* ============================= */}

          {!loading && exams.length > 0 && slots.length === 0 && (
            <p className="text-muted small mb-0">
              No slots scheduled yet.
            </p>
          )}

          {/* ============================= */}
          {/* TABLE */}
          {/* ============================= */}

          {!loading && slots.length > 0 && (
            <>
              <div className="table-responsive">
                <table className="table align-middle admin-table mb-0">

                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Date</th>
                      <th>Start Time</th>
                      <th>End Time</th>
                      <th>Capacity</th>
                      <th>Booked</th>
                      <th>Status</th>
                      <th className="text-end">
                        Actions
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {currentSlots.map((s, i) => (
                      <tr key={s.id}>

                        {/* Correct serial number across pages */}
                        <td className="text-muted">
                          {startIndex + i + 1}
                        </td>

                        <td>
                          {s.date}
                        </td>

                        <td>
                          {s.startTime}
                        </td>

                        <td>
                          {s.endTime}
                        </td>

                        <td>
                          {s.capacity}
                        </td>

                        <td>
                          {s.booked}
                        </td>

                        <td>
                          <button
                            className={`admin-status-pill border-0 ${
                              s.status === "Active"
                                ? "active"
                                : "inactive"
                            }`}
                            style={{
                              cursor: "pointer",
                            }}
                            onClick={() =>
                              toggleStatus(s)
                            }
                          >
                            {s.status}
                          </button>
                        </td>

                        <td className="text-end">
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() =>
                              handleDelete(s.id)
                            }
                            title="Delete"
                          >
                            <i className="bi bi-trash" />
                          </button>
                        </td>

                      </tr>
                    ))}
                  </tbody>

                </table>
              </div>

              {/* ============================= */}
              {/* PAGINATION */}
              {/* ============================= */}

              <div className="d-flex flex-wrap justify-content-between align-items-center mt-3">

                {/* Showing text */}
                <div className="text-muted small mb-2 mb-md-0">
                  Showing{" "}
                  <strong>
                    {startIndex + 1}
                  </strong>{" "}
                  to{" "}
                  <strong>
                    {Math.min(
                      startIndex + ITEMS_PER_PAGE,
                      slots.length
                    )}
                  </strong>{" "}
                  of{" "}
                  <strong>{slots.length}</strong>{" "}
                  slots
                </div>

                {/* Pagination */}
                <nav>
                  <ul className="pagination pagination-sm mb-0">

                    {/* Previous */}
                    <li
                      className={`page-item ${
                        currentPage === 1
                          ? "disabled"
                          : ""
                      }`}
                    >
                      <button
                        className="page-link"
                        onClick={() =>
                          goToPage(currentPage - 1)
                        }
                        disabled={currentPage === 1}
                      >
                        <i className="bi bi-chevron-left" />
                      </button>
                    </li>

                    {/* Page Numbers */}
                    {getPageNumbers().map((page) => (
                      <li
                        key={page}
                        className={`page-item ${
                          currentPage === page
                            ? "active"
                            : ""
                        }`}
                      >
                        <button
                          className="page-link"
                          onClick={() =>
                            goToPage(page)
                          }
                        >
                          {page}
                        </button>
                      </li>
                    ))}

                    {/* Next */}
                    <li
                      className={`page-item ${
                        currentPage === totalPages
                          ? "disabled"
                          : ""
                      }`}
                    >
                      <button
                        className="page-link"
                        onClick={() =>
                          goToPage(currentPage + 1)
                        }
                        disabled={
                          currentPage === totalPages
                        }
                      >
                        <i className="bi bi-chevron-right" />
                      </button>
                    </li>

                  </ul>
                </nav>

              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}